/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, {  useEffect, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
    func, number, object, string,
} from 'prop-types';
import { useUIDSeed } from 'react-uid';
import Slider from 'react-slick';
import ReactHtmlParser from 'react-html-parser';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIosTwoTone';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIosTwoTone';
import { useDispatch } from 'react-redux';
import Button from '@material-ui/core/Button';
import { selectedSkuType } from '../common/types';
import ResponsiveImage from '../../../GraphqlCommonComponents/ResponsiveImage/ResponsiveImage';
import GraphqlClubProductDetails from './GraphqlClubProductDetails';
import { trackEvent } from '../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';

const useStyles = makeStyles((theme) => ({
    carouselContainer: {
        position: 'relative',
        width: '100%',
        '@media only screen and (max-width: 600.5px) ': {
            paddingBottom: '0px',
        },
        '& .slick-disabled': {
            display: 'none !important',
        },
        '& .slick-list': {
            overflow: 'hidden',
            margin: '0 auto',
            width: '100%',
            '&::-webkit-scrollbar-thumb': {
                borderRadius: '10px',
                backgroundColor: 'rgba(156, 156, 156, 1)',  // scroll bar color
            },
            '&::-webkit-scrollbar': {  /* Works on scrollbar for Chrome, Safari and Opera */
                height: '3px',
                backgroundColor: 'transparent',
            },
            '&::-webkit-scrollbar-track': {
                borderRadius: '10px',
                backgroundColor: 'transparent',
            },
        },
        '& .slick-slide': {
            display: 'inline-block',
            verticalAlign: 'top',
        },
        '& .slick-next': {
            right: '2%',
            borderRadius: '50%',
            width: 30,
            background: theme.palette.headerColor || '#E8F1ED',
            transform: 'translate(0,-50%)',
            transition: 'all .2s ease-out',
            cursor: 'pointer',
            outline: 0,
            fontSize: 0,
            lineHeight: 0,
            top: '50%',
            height: 30,
            position: 'absolute',
            '@media only screen and (max-width: 600px) ': {
                display: 'none !important',
            },
            '& svg': {
                width: '0.6em',
                position: 'absolute',
                right: '18%',
            },
        },
        '& .slick-prev': {
            zIndex: '1',
            left: '2%',
            border: `1px solid ${theme.palette.headerColor || '#E8F1ED'}`,
            borderRadius: '50%',
            width: 30,
            background: theme.palette.headerColor || '#E8F1ED',
            transform: 'translate(0,-50%)',
            transition: 'all .2s ease-out',
            cursor: 'pointer',
            outline: 0,
            fontSize: 0,
            lineHeight: 0,
            top: '50%',
            height: 30,
            position: 'absolute',
            '@media only screen and (max-width: 600px) ': {
                display: 'none !important',
            },
            '& svg': {
                width: '0.65em',
                position: 'absolute',
                right: '18%',
            },

        },
        '& .slick-track': {
            display: 'flex',
            transition: 'all .2s ease-out',
            scrollBehavior: 'smooth',
        },
    },
    flexCenter: {
        display: 'flex',
        justifyContent: 'center',
    },
    altImage: {
        width: '100%',
        height: '100%',
        aspectRatio: '107/94',
        '@media only screen and (max-width: 400px) ': {
            maxHeight: '94px',
        },
    },
    monthSnipe: {
        display: 'block',
        fontSize: '0.875rem',
        lineHeight: '1.125rem',
    },

    viewDetails: {
        display: 'block',
        textDecoration: 'underline',
        fontSize: '0.750rem',
        lineHeight: '1.125rem',
        fontWeight: 400,
        cursor: 'pointer',
        textTransform: 'capitalize',
        textAlign: 'left',
        padding: '0px',
        color: theme.palette.cms?.black || '#000000',
        '&:hover': {
            textDecoration: 'underline',
            backgroundColor: 'inherit',
        },
    },
    productName: {
        fontSize: '0.750rem',
        lineHeight: '1.125rem',
        fontWeight: 400,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: '-webkit-box',
        WebkitLineClamp: '2',
        WebkitBoxOrient: 'vertical',
        minHeight: '35px',
    },
    clubmonth: {
        display: 'block',
        cursor: 'pointer',
        backgroundColor: theme.palette.cms?.white || '#ffffff',
        padding: '8px',
        boxShadow: '0px 0px 5px rgb(47 47 47 / 25%)',
    },
    clubSelectedMonth: {
        boxShadow: '0px 0px 0px 2px rgba(63, 141, 93, 1)',
    },
    selectedUpsell: {
        position: 'relative',
    },
    upgraderd: {
        position: 'absolute',
        fontSize: '0.75rem',
        lineHeight: '1.125rem',
        color: theme.palette.cms?.white || '#ffffff',
        backgroundColor: theme.palette.cms?.green || 'rgba(63, 141, 93, 1)',
        top: '0px',
        left: '0px',
        padding: '0px 5px',
    },
    selectedClubMonths: {
        backgroundColor: 'rgba(63, 141, 93, 0.2)',
        padding: '6px',
    },
    fadeClubMonth: {
        padding: '6px',
    },
}));

const GraphqlClubMonth = ({
    itemsPerSlide, itemsToShow,  skuMonths, selectedSku, changeClubMonth, selectedClubUpsells, product,
}) => {
    const classes = useStyles();
    const seed = useUIDSeed();
    const [open, setOpen] = useState(false);
    const dispatch = useDispatch();
    const [data, setData] = useState({});
    const isSelectedMonth = selectedSku?.date?.month?.toLowerCase() === data?.month?.toLowerCase();
    const buttonContent = isSelectedMonth ? 'Selected' : 'Select';
    const tracker = (matchObject) => {
        dispatch(trackEvent({
            eventAction: 'club length',
            eventLabel: matchObject?.month,
            eventCategory: 'Product Page',
        }));
    };
    const getSlides = () => {
        if (product?.club?.defaultSku?.components?.length > 0 && skuMonths) {
            let selectedCount = 1;
            let isSelected = false;
            return product?.club?.defaultSku?.components.map((matchObject) => {
                let changeClubMonthLength = 0;
                const tempSelectedMonth = skuMonths[matchObject?.month];
                const currentClubLength = Number(selectedSku?.clubLength) || 0;
                const nextMonthSameLength = tempSelectedMonth?.findIndex((monthSku) => Number(monthSku?.clubLength) === currentClubLength);
                if (nextMonthSameLength >= 0) {
                    changeClubMonthLength = nextMonthSameLength;
                }
                // Check if the selected month matches the month in the match object
                const isSelectedMonthValid = tempSelectedMonth?.[0]?.isSelectedMonth;
                const isMatchingMonth = selectedSku?.date?.month === matchObject?.month;

                // Determine if the current item should be selected
                if (isSelectedMonthValid && isMatchingMonth) {
                    isSelected = true;
                    selectedCount += 1;
                } else if (currentClubLength >= selectedCount && isSelected) {
                    selectedCount += 1;
                    isSelected = true;
                } else {
                    isSelected = false;
                }

                return (
                    <div className={`${isSelected ? classes.selectedClubMonths : classes.fadeClubMonth} `} key={seed(matchObject)} data-testid={`selected-${matchObject?.month}`}>
                        <div className={`${tempSelectedMonth?.[0]?.isSelectedMonth ? `${classes.clubSelectedMonth} ${classes.clubmonth} ${selectedClubUpsells && classes.selectedUpsell}` : classes.clubmonth}`}>
                            <div
                                data-testid={`next-selected-${matchObject?.month}-${changeClubMonthLength}-${tempSelectedMonth?.[changeClubMonthLength]?.id}`}
                                onClick={() => {
                                    changeClubMonth(tempSelectedMonth?.[changeClubMonthLength]);
                                }}
                                data-year={tempSelectedMonth?.[0]?.date?.year}
                                tabIndex="0"
                                role="link"
                                onKeyPress={() => { }}
                            >
                                <div className={classes.mainContainer}>
                                    {tempSelectedMonth?.[0]?.isSelectedMonth && selectedClubUpsells && <span className={classes.upgraderd}>Upgraded</span>}
                                    <ResponsiveImage
                                        path={`${matchObject?.image?.path}${matchObject?.image?.name}x.jpg?width=107&height=94&auto=webp`}
                                        alt={ReactHtmlParser(`${matchObject?.month} club product`)}
                                        className={classes.altImage}
                                    />
                                    <span className={classes.monthSnipe}><strong>{matchObject?.month}</strong></span>
                                    <span className={classes.productName}>{ReactHtmlParser(matchObject?.name)}</span>
                                </div>
                            </div>
                            <Button
                                className={classes.viewDetails}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    tracker(matchObject);
                                    setOpen(true);
                                    setData(matchObject);
                                }}
                            >
                                View Details
                            </Button>
                        </div>
                    </div>
                );
            });
        }
        return [];
    };
    const sliderRef = useRef();
    const indexMatch = product?.club?.defaultSku?.components.findIndex((item) => item?.month === data?.month);
    const selectedMonth = Object.keys(skuMonths).filter((item) => skuMonths[item]?.[0]?.isSelectedMonth);
    const productsSelected = product?.club?.defaultSku?.components?.findIndex((item) => item.month === selectedMonth[0]);
    useEffect(() => {
        if (sliderRef?.current) {
            sliderRef.current.slickGoTo(productsSelected);
        }
    }, [productsSelected]);
    return (
        <div className={classes.flexCenter}>
            <Slider
                dots={false}
                speed={500}
                ref={sliderRef}
                infinite={false}
                slidesToShow={itemsToShow}
                slidesToScroll={itemsPerSlide}
                initialSlide={productsSelected}
                nextArrow={<div><ArrowForwardIosIcon /></div>}
                prevArrow={<div><ArrowBackIosIcon /></div>}
                className={`carousel-container ${classes.carouselContainer}`}
                responsive={[
                    {
                        breakpoint: 1024, // tablet breakpoint
                        settings: {
                            slidesToShow: 3.5,
                            slidesToScroll: 3,
                        },
                    },
                    {
                        breakpoint: 767, // mobile breakpoint
                        settings: {
                            slidesToShow: 2.75,
                            slidesToScroll: 2,
                        },
                    },
                ]}
            >
                {getSlides().map((item) => (
                    <div key={seed(item)}>
                        { item }
                    </div>
                ))}
            </Slider>
            {open && (
                <GraphqlClubProductDetails
                    open={open}
                    data={data}
                    setOpen={setOpen}
                    indexMatch={indexMatch}
                    reciepientProduct={product?.club?.defaultSku?.components}
                    setData={setData}
                    title="STARTING MONTH DETAILS"
                    monthDetails
                    onclick={changeClubMonth}
                    buttonContent={buttonContent}
                    disabled={isSelectedMonth}
                    skuMonths={skuMonths}
                    selectedSku={selectedSku}
                />
            )}
        </div>
    );
};
GraphqlClubMonth.propTypes = {
    itemsToShow: number,
    itemsPerSlide: number,
    changeClubMonth: func,
    skuMonths: object,
    selectedSku: selectedSkuType,
    selectedClubUpsells: string,
    product: object,

};
GraphqlClubMonth.defaultProps = {
    skuMonths: {},
    itemsToShow: 4,
    itemsPerSlide: 4,
    changeClubMonth: () => { },
    selectedSku: {},
    selectedClubUpsells: '',
    product: {},
};
export default GraphqlClubMonth;
